import { Component, EventEmitter, Input, Output, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgSelectModule } from '@ng-select/ng-select';
import { ModelService } from '../../../../core/services/model/model.service';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { debounceTime, filter, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthStateService } from '../../../../core/services/auth-state/auth-state-service.service';
import { CampaignsService } from '../../../../core/services/campaigns/campaigns.service';
import { FormatNumberPipe, FormatNumberPipe2 } from '../../../../core/pipe/custom-pipes';

@Component({
  selector: 'app-contract-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    NgSelectModule,
    CommonModule,
    FormatNumberPipe,
    FormatNumberPipe2,
  ],
  templateUrl: './contract-form.component.html',
  styleUrl: './contract-form.component.css',
})
export class ContractFormComponent implements OnDestroy {
  @Input() orderData?: any;
  @Input() ladata: any = [];

  private destroy$ = new Subject<void>();
  @Input() showEditConfigButton: boolean = false;
  @Output() editConfigClick = new EventEmitter<void>();

  contractForm: FormGroup = new FormGroup({});
  type_: any[] = ['renting', 'leasing'];
  durationOptions: string[] = ['12', '24', '36', '48', '60', '72'];

  // Config selector state
  showConfigSelector = false;
  loadingMatrix = false;
  matrixData: { matrix_base: any; matrix_total: any; matrix_tax: string } | null = null;
  matrixColumns: string[] = [];
  matrixRows: string[] = [];
  matrixDisplay: any = {};
  showIva = true;
  selectedCol = '';
  selectedRow = '';

  get taxRate(): number {
    return this.authStateService.getSupplierTax() || 0.21;
  }

  get hasCampaign(): boolean {
    return !!this.orderData?.campaign_id;
  }

  get isBroker(): boolean {
    return this.authStateService.getAccountType() === 'broker';
  }

  onEditConfigClick(): void {
    this.editConfigClick.emit();
  }

  constructor(private fb: FormBuilder, private alertService: AlertService, private authStateService: AuthStateService, private campaignsService: CampaignsService) {
    this.contractForm = this.fb.group({
      duration: ['12'],
      deposit: ['', Validators.required],
      deposit_paid: [false, Validators.required],
      include_insurance: [false, Validators.required],
      include_maintenance: [false, Validators.required],
      include_tires: [false, Validators.required],
      initial_payment: ['', Validators.required],
      initial_payment_paid: [false, Validators.required],
      prepayment: ['', Validators.required],
      prepayment_paid: [false, Validators.required],
      transport: ['', Validators.required],
      transport_paid: [false, Validators.required],
      monthly_fee_base: [0.0, Validators.required],
      monthly_fee_tax: [0.0, Validators.required],
      monthly_fee_total: [0.0, Validators.required],
      total_kms: ['', Validators.required],
      include_replacement_vehicle: [false, Validators.required],
      insurance_deductible: [null],
      insurance_notes: [''],
      maintenance_notes: [''],
      tires_notes: [''],
      replacement_vehicle_notes: [''],
      type: [''],
      yearly_kms: [''],
    });
  }

  ngOnInit(): void { }

  ngAfterContentInit() {
    this.addDebouncedValue();
  }

  addDebouncedValue() {
    this.addDebouncedValueChange(this.contractForm.get('total_kms'), () =>
      this.calculateTotalKms()
    );
    this.addDebouncedValueChange(this.contractForm.get('yearly_kms'), () =>
      this.calculateAnualKms()
    );
    this.addDebouncedValueChange(
      this.contractForm.get('monthly_fee_base'),
      () => this.calculateMonthlyFeesFromBase()
    );
    this.addDebouncedValueChange(this.contractForm.get('monthly_fee_tax'), () =>
      this.calculateMonthlyFeesFromTax()
    );
    this.addDebouncedValueChange(
      this.contractForm.get('monthly_fee_total'),
      () => this.calculateMonthlyFeesFromTotal()
    );
  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes['orderData'] && changes['orderData'].currentValue) {

      this.refrescarFormulario();


    }

    /*  if (changes['ladata'] && changes['ladata'].currentValue && this.orderData) {
       this.refrescarFormulario();
     } */
  }

  private addDebouncedValueChange(control: any, callback: () => void): void {
    control?.valueChanges.pipe(debounceTime(1000), takeUntil(this.destroy$)).subscribe(callback);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  refrescarFormulario() {
    this.updateFieldAccessibility();

    for (const key in this.orderData) {
      const control = this.contractForm.controls[key];
      const value = this.orderData[key];
      if (control) {
        control.setValue(value, { emitEvent: false });
      }
    }
    this.alertService.close();
  }

  updateFieldAccessibility() {
    var rules = this.orderData?.rules;
    // Deshabilitar todos los campos por defecto
    for (const field in this.contractForm.controls) {
      if (this.contractForm.controls.hasOwnProperty(field)) {
        this.contractForm.get(field)?.disable();
      }
    }

    // Habilitar solo los campos con valor 'w' en las reglas
    for (const field in rules) {
      if (rules.hasOwnProperty(field) && rules[field] === 'w') {
        this.contractForm.get(field)?.enable();
      }
    }
  }

  changeType(value: any) {
    var type: any;
    if (typeof value === 'string') {
      type = value;
    } else {
      type = value.target.value;
    }

    if (type == 'renting') {
      this.contractForm.get('include_tires')?.setValue(1);
      this.contractForm.get('include_insurance')?.setValue(1);
      this.contractForm.get('include_maintenance')?.setValue(1);
      this.contractForm.get('include_replacement_vehicle')?.setValue(0);
    } else {
      this.contractForm.get('include_tires')?.setValue(0);
      this.contractForm.get('include_insurance')?.setValue(1);
      this.contractForm.get('include_maintenance')?.setValue(1);
      this.contractForm.get('include_replacement_vehicle')?.setValue(0);
    }
  }

  calculateTotalKms(): void {

    const months = parseInt(this.contractForm.get('duration')?.value);
    const kms = parseInt(this.contractForm.get('total_kms')?.value);
    if (!isNaN(kms) && !isNaN(months)) {
      const base = Math.round((kms / months) * 12);

      this.contractForm.get('yearly_kms')?.setValue(base, { emitEvent: false });
    }
  }

  calculateAnualKms(): void {
    const months = parseInt(this.contractForm.get('duration')?.value);
    const kms = parseInt(this.contractForm.get('yearly_kms')?.value);
    if (!isNaN(kms) && !isNaN(months)) {
      const base = Math.round(kms * (months / 12));

      this.contractForm.get('total_kms')?.setValue(base, { emitEvent: false });
    }
  }

  calculateKms(): void {
    if (this.orderData?.total_kms) {
      this.contractForm
        .get('total_kms')
        ?.setValue(this.orderData?.total_kms, { emitEvent: false });
      this.calculateTotalKms();
    } else {
      this.contractForm.get('total_kms')?.setValue('', { emitEvent: false });
      this.contractForm.get('yearly_kms')?.setValue('', { emitEvent: false });
    }
  }

  calculateMonthlyFeesFromBase(): void {
    const base = parseFloat(this.contractForm.get('monthly_fee_base')?.value);
    if (!isNaN(base)) {
      const baseRounded = parseFloat(base.toFixed(2));
      const tax = parseFloat((baseRounded * this.taxRate).toFixed(2));
      const total = parseFloat((baseRounded * (1 + this.taxRate)).toFixed(2));

      this.contractForm.get('monthly_fee_base')?.setValue(baseRounded, { emitEvent: false });
      this.contractForm.get('monthly_fee_tax')?.setValue(tax, { emitEvent: false });
      this.contractForm.get('monthly_fee_total')?.setValue(total, { emitEvent: false });
    }
  }

  calculateMonthlyFeesFromTax(): void {
    const tax = parseFloat(this.contractForm.get('monthly_fee_tax')?.value);
    if (!isNaN(tax)) {
      const taxRounded = parseFloat(tax.toFixed(2));
      const base = parseFloat((taxRounded / this.taxRate).toFixed(2));
      const total = parseFloat((base * (1 + this.taxRate)).toFixed(2));

      this.contractForm.get('monthly_fee_tax')?.setValue(taxRounded, { emitEvent: false });
      this.contractForm.get('monthly_fee_base')?.setValue(base, { emitEvent: false });
      this.contractForm.get('monthly_fee_total')?.setValue(total, { emitEvent: false });
    }
  }

  calculateMonthlyFeesFromTotal(): void {
    const total = parseFloat(this.contractForm.get('monthly_fee_total')?.value);
    if (!isNaN(total)) {
      const totalRounded = parseFloat(total.toFixed(2));
      const base = parseFloat((totalRounded / (1 + this.taxRate)).toFixed(2));
      const tax = parseFloat((base * this.taxRate).toFixed(2));

      this.contractForm.get('monthly_fee_total')?.setValue(totalRounded, { emitEvent: false });
      this.contractForm.get('monthly_fee_base')?.setValue(base, { emitEvent: false });
      this.contractForm.get('monthly_fee_tax')?.setValue(tax, { emitEvent: false });
    }
  }

  checkUpdateForm(): boolean {
    if (!this.contractForm.dirty) {
      return false;
    }
    const currentValues = this.contractForm.getRawValue();
    const originalValues = this.orderData;

    const numericFields = [
      'monthly_fee_base',
      'monthly_fee_tax',
      'monthly_fee_total',
    ];

    for (const field of numericFields) {
      const current = parseFloat(currentValues[field]) || 0;
      const original = parseFloat(originalValues[field]) || 0;
      if (Math.abs(current - original) > 0.01) {
        return true;
      }
    }
    return this.contractForm.dirty;
  }

  fillContractFormForHelper(option: any) {
    const noEmit = { emitEvent: false };
    this.contractForm.get('type')?.setValue(option['type'], noEmit);
    this.contractForm.get('duration')?.setValue(option['duration'], noEmit);
    this.contractForm.get('total_kms')?.setValue(option['total_kms'], noEmit);
    this.contractForm
      .get('monthly_fee_total')
      ?.setValue(option['monthly_fee_total'], noEmit);
    this.contractForm
      .get('monthly_fee_base')
      ?.setValue(option['monthly_fee_base'], noEmit);
    this.contractForm
      .get('monthly_fee_tax')
      ?.setValue(option['monthly_fee_tax'], noEmit);
    this.contractForm.get('include_tires')?.setValue(option['include_tires'], noEmit);
    this.contractForm
      .get('include_insurance')
      ?.setValue(option['include_insurance'], noEmit);
    this.contractForm
      .get('include_maintenance')
      ?.setValue(option['include_maintenance'], noEmit);
    this.contractForm
      .get('include_replacement_vehicle')
      ?.setValue(option['include_replacement_vehicle'], noEmit);
  }

  // --- Config selector methods ---

  toggleConfigSelector(): void {
    if (this.showConfigSelector) {
      this.showConfigSelector = false;
      return;
    }
    this.loadMatrix();
  }

  loadMatrix(): void {
    const campaignRef = this.orderData?.campaign?.reference || this.orderData?.campaign_reference;
    if (!campaignRef) return;

    this.loadingMatrix = true;
    this.campaignsService.getMatrix(campaignRef).subscribe({
      next: (res: any) => {
        this.matrixData = res.data;
        this.updateMatrixDisplay();
        this.showConfigSelector = true;
        this.loadingMatrix = false;

        // Pre-select current config
        const currentDuration = String(this.contractForm.getRawValue().duration);
        const currentKms = String(this.contractForm.getRawValue().yearly_kms);
        if (this.matrixDisplay[currentDuration]?.[currentKms] != null) {
          this.selectedCol = currentDuration;
          this.selectedRow = currentKms;
        }
      },
      error: () => {
        this.loadingMatrix = false;
      }
    });
  }

  updateMatrixDisplay(): void {
    if (!this.matrixData) return;
    const matrix = this.showIva ? this.matrixData.matrix_total : this.matrixData.matrix_base;
    if (!matrix) {
      this.matrixColumns = [];
      this.matrixRows = [];
      this.matrixDisplay = {};
      return;
    }
    this.matrixDisplay = matrix;
    this.matrixColumns = Object.keys(matrix).sort((a, b) => +a - +b);
    const kmSet = new Set<string>();
    Object.values(matrix).forEach(row => {
      Object.keys(row as object).forEach(km => kmSet.add(km));
    });
    this.matrixRows = Array.from(kmSet).sort((a, b) => +a - +b);
  }

  onIvaToggle(event: Event): void {
    this.showIva = (event.target as HTMLInputElement).checked;
    this.updateMatrixDisplay();
  }

  selectCell(col: string, row: string): void {
    this.selectedCol = col;
    this.selectedRow = row;

    const matrixBase = this.matrixData?.matrix_base;
    const matrixTotal = this.matrixData?.matrix_total;
    const baseRaw = matrixBase?.[col]?.[row];
    const totalRaw = matrixTotal?.[col]?.[row];

    let base: number, tax: number, total: number;
    if (baseRaw != null && totalRaw != null) {
      base = +baseRaw;
      total = +totalRaw;
      tax = +(total - base).toFixed(2);
    } else if (baseRaw != null) {
      base = +baseRaw;
      tax = +(base * this.taxRate).toFixed(2);
      total = +(base + tax).toFixed(2);
    } else if (totalRaw != null) {
      total = +totalRaw;
      base = +(total / (1 + this.taxRate)).toFixed(2);
      tax = +(total - base).toFixed(2);
    } else {
      return; // No data for this cell
    }

    const duration = +col;
    const yearlyKms = +row;
    const totalKms = Math.round(yearlyKms * (duration / 12));

    const noEmit = { emitEvent: false };
    // Temporarily enable fields that are read-only by rules
    const configFields = ['duration', 'yearly_kms', 'total_kms', 'monthly_fee_base', 'monthly_fee_tax', 'monthly_fee_total'];
    configFields.forEach(f => this.contractForm.get(f)?.enable());

    this.contractForm.get('duration')?.setValue(String(duration), noEmit);
    this.contractForm.get('yearly_kms')?.setValue(String(yearlyKms), noEmit);
    this.contractForm.get('total_kms')?.setValue(String(totalKms), noEmit);
    this.contractForm.get('monthly_fee_base')?.setValue(+(base.toFixed(2)), noEmit);
    this.contractForm.get('monthly_fee_tax')?.setValue(+(tax.toFixed(2)), noEmit);
    this.contractForm.get('monthly_fee_total')?.setValue(+(total.toFixed(2)), noEmit);

    this.contractForm.markAsDirty();
  }
}
